import { useMutation } from "@tanstack/react-query";
import { t } from "ttag";

import { ROUTES } from "@/app/router";
import { useCreateShopPayments, useKlarna } from "@/features/payments/hooks";
import type { CreditAmount } from "@/features/referral/models";
import { PAYMENT_METHOD_TYPE } from "@/shared/domains";
import type { Price } from "@/shared/domains";
import { COUNTRY } from "@/shared/models";
import { errorToast } from "@/shared/ui";

interface UsePayWithKlarnaParams {
  price?: Price;
  referralCredit?: CreditAmount;
}

export const usePayWithKlarna = ({
  price,
  referralCredit,
}: UsePayWithKlarnaParams) => {
  const createPaymentsMutation = useCreateShopPayments();

  const { pay, available } = useKlarna({
    price,
  });

  return useMutation({
    mutationFn: async () => {
      if (!price) {
        throw new Error(t`Price information is missing`);
      }

      if (!available) {
        throw new Error(t`Klarna is not available for the selected price`);
      }

      const createdPayments = await createPaymentsMutation.mutateAsync({
        prices: [price.id],
        discount: price.discount?.code,
        referralCredit,
        paymentMethod: PAYMENT_METHOD_TYPE.KLARNA,
      });

      const paymentToPay = createdPayments[0];

      if (!paymentToPay) {
        throw new Error(
          t`There was a problem creating your subscription. Try again later or contact support.`,
        );
      }

      const { clientSecret } = paymentToPay;

      if (!clientSecret) {
        return paymentToPay.subscription;
      }

      const returnUrl = new URL(
        ROUTES.BILLING.OVERVIEW,
        window.location.origin,
      ).toString();

      await pay({
        clientSecret,
        billingDetails: {
          address: {
            country: COUNTRY.US,
          },
        },
        returnUrl,
      });
    },
    onError: errorToast,
  });
};
